// @flow
import * as React from 'react';
import { sprintf } from 'sprintf-js';
import styled, { css } from 'styled-components';
import type { FileUploadTypes } from 'api/shared';
import { isDefined } from 'utils/functional';
import { getRgbaString } from 'utils/style';
import { fileIdToUrl } from 'utils/s3file';
import { dateTime } from 'utils/date';
import Box from 'components/Box';
import S3Picker from 'containers/S3Picker';
import Spinner from 'components/Spinner';
import Button from 'components/Button';
import { Body, BodySmall } from 'components/Text';
import Icon from 'components/Icon';
import ViewportContext from 'containers/ViewportContext';
import { colors, transition, mediaRetina, breakpoints } from '../theme';

const { useContext, useRef, useEffect, useState } = React;

const Svg = styled(Box)`
  rect {
    fill: ${colors.platinum};
    stroke: ${colors.silver};
    stroke-width: 1px;
    stroke-dasharray: 9px 7px;
    rx: 5px;
    transition: all ${transition};
    transition-property: stroke, fill;
  }
`;

const DeleteButton = styled(Button)`
  transition: all ${transition};
  color: ${colors.battleshipGrey};
  &:hover,
  &:focus {
    color: ${colors.waterBlue};
  }
  ${props =>
    props.$large &&
    `
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  box-shadow: 0 0 0 2px transparent inset;
  &:hover {
    color: ${colors.scarlet};
    box-shadow: 0 0 0 2px ${colors.scarlet} inset;
  } 
  `}
`;

const DropBox = styled(Box)`
  color: ${props =>
    props.$hasFile || props.$large ? colors.battleshipGrey : colors.waterBlue};
  transition: all ${transition};
  & > * {
    position: relative;
    ${props =>
      props.$large
        ? ''
        : `
    border: 1px solid ${props.$hasFile ? colors.silver : colors.waterBlue};
    ${mediaRetina} {
      border-width: 1.5px;
    }
    `}
    border-radius: ${props => (props.$large ? 10 : 45)}px;
    transition: all ${transition};
  }
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 6px;
    opacity: 0;
    transition: all ${transition};
  }
`;

const DropContainer = styled(Box)`
  &,
  * {
    pointer-events: none;
  }
  ${DeleteButton}, a {
    pointer-events: auto;
  }
  ${props =>
    props.$mouseActive &&
    css`
      ${DropBox} {
        & > * {
          border-color: ${colors.waterBlue};
          ${props.$large
            ? `
          color: ${colors.waterBlue};
          `
            : `
          background: ${colors.waterBlue};
          color: white;
          `}
        }
        &::before {
          opacity: 1;
        }
      }
      ${Svg} {
        rect {
          stroke: ${colors.waterBlue};
          fill: transparent;
        }
      }
    `}
  ${props =>
    props.$dragActive &&
    `
    * {
      pointer-events: none !important;
    }
  `}
`;

const FileLink = styled.a`
  ${props =>
    props.$large &&
    `
  color: ${colors.greyishBrown};
  `}
`;

const FilenameText = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: middle;
  ${props =>
    props.$large
      ? `
    max-width: 300px;
    @media (min-width: ${breakpoints[0]}) {
      max-width: none;
      overflow: visible;
      white-space: normal;
      text-overflow: clip;
    }
  `
      : `
    max-width: 200px;
  `}
`;

const Filename = ({
  fileId,
  large,
  qaId,
  downloadAttachment,
  filename,
  hideRemove,
  onChange,
  created,
  disabled
}: {
  fileId: string,
  large?: boolean,
  qaId: string,
  downloadAttachment?: boolean,
  filename: ?string,
  hideRemove?: boolean,
  onChange: (?string) => void,
  onError?: Error => void,
  created?: string,
  disabled?: boolean
}) => (
  <Box>
    <Box
      component={Body}
      position="relative"
      display="flex"
      alignItems="center"
      data-qa-id={`${qaId}-preview`}
    >
      <FileLink
        title={
          downloadAttachment
            ? getText('Download')
            : getText('Open in new window')
        }
        href={fileIdToUrl(fileId, downloadAttachment)}
        target="_blank"
        onClick={e => {
          e.stopPropagation();
        }}
        rel="noopener noreferrer"
        $large={large}
      >
        <FilenameText $large={large}>{filename}</FilenameText>
      </FileLink>

      {!hideRemove && !disabled && (
        <Box ml={10}>
          <DeleteButton
            title={
              /* TRANSLATORS: Remove file upload */ sprintf(
                getText('Remove file %(filename)s'),
                {
                  filename
                }
              )
            }
            data-qa-id={`${qaId}-delete`}
            onClick={e => {
              e.stopPropagation();
              onChange(undefined);
            }}
            $large={large}
          >
            <Icon
              name="close"
              display="block"
              width={10}
              color="currentColor"
            />
          </DeleteButton>
        </Box>
      )}
    </Box>

    {created && (
      <Box width={1} flex="0 0 auto">
        <BodySmall
          color={colors.battleshipGrey}
          whiteSpace="nowrap"
          lineHeight={1.25}
          data-qa-id={`${qaId}-upload-date`}
        >
          {sprintf(getText('Last updated: %(date)s'), {
            date: dateTime(created)
          })}
        </BodySmall>
      </Box>
    )}
  </Box>
);

type Props = {
  downloadAttachment?: boolean,
  fileId: ?string,
  fileType: FileUploadTypes,
  message?: string,
  name: string,
  onChange: (?string) => void,
  onError?: Error => void,
  hideRemove?: boolean,
  isRequired?: boolean,
  'data-qa-id': string,
  value?: string,
  'aria-label'?: string,
  hasError?: boolean,
  large?: boolean,
  showDate?: number,
  accept?: string,
  disabled?: boolean
};

const FilePicker = ({
  downloadAttachment,
  fileId,
  fileType,
  message = getText('Drag file here or browse'),
  name,
  onChange,
  onError,
  'data-qa-id': qaId,
  hideRemove,
  isRequired = false,
  value,
  'aria-label': ariaLabel,
  hasError,
  large,
  showDate,
  accept,
  disabled,
  ...props
}: Props) => {
  const [viewportWidth] = useContext(ViewportContext);
  const dropboxRef = useRef(null);
  const [aspectRatio, setAspectRatio] = useState(null);

  useEffect(
    () => {
      if (dropboxRef && dropboxRef.current) {
        const box = dropboxRef.current.getBoundingClientRect();
        setAspectRatio(box.width / box.height);
      }
    },
    [viewportWidth]
  );

  return (
    <S3Picker
      downloadAttachment={downloadAttachment}
      isRequired={isRequired}
      fileId={fileId}
      fileType={fileType}
      name={name}
      onChange={onChange}
      onError={onError}
      qaId={qaId}
      value={value}
      aria-label={ariaLabel}
      accept={accept}
      disabled={disabled}
    >
      {({ onClick, filename, created, loading, dragActive, mouseActive }) => (
        <DropContainer
          position="relative"
          display="inline-flex"
          width={1}
          flexWrap="wrap"
          alignItems="center"
          pointerEvents="none"
          $hasFile={isDefined(fileId)}
          $hasError={hasError}
          $dragActive={dragActive}
          $mouseActive={mouseActive}
          $large={large}
        >
          <Box
            position="absolute"
            left={-12}
            right={-12}
            top={-12}
            bottom={-12}
            borderRadius={large ? '16px' : '4px'}
            bg={`rgba(${getRgbaString(colors.babyBlue, 0.2)})`}
            opacity={mouseActive && !loading ? 1 : 0}
            transition={`opacity ${transition}`}
          />
          {loading && !large ? (
            <Box
              width={32}
              height={65}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Spinner size="small" />
            </Box>
          ) : (
            <>
              {large ? (
                <Box width={1} flex="0 0 auto">
                  <DropBox
                    position="relative"
                    onClick={onClick}
                    $hasFile={!!fileId}
                    $large
                    {...props}
                  >
                    <div
                      ref={dropboxRef}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        height: '192px',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '6px 16px'
                      }}
                    >
                      {aspectRatio && (
                        <Svg
                          component="svg"
                          position="absolute"
                          top={0}
                          left={0}
                          width={1}
                          viewBox={`0 0 ${100 * aspectRatio} 100`}
                        >
                          <rect
                            x={1}
                            y={1}
                            width={100 * aspectRatio - 2}
                            height={98}
                          />
                        </Svg>
                      )}
                      {loading ? (
                        <Box
                          width={1}
                          height={192}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Spinner size="default" />
                        </Box>
                      ) : (
                        <>
                          <Box position="relative">
                            <Icon
                              name="paperclip"
                              display="block"
                              width={32}
                              fill={colors.waterBlue}
                            />
                          </Box>
                          <Box
                            position="relative"
                            ml={12}
                            fontSize={[20, null, null, 24]}
                          >
                            {fileId ? (
                              <Filename
                                fileId={fileId}
                                filename={filename}
                                qaId={qaId}
                                hideRemove={hideRemove}
                                onChange={onChange}
                                created={showDate ? created : undefined}
                                downloadAttachment={downloadAttachment}
                                disabled={disabled}
                                large
                              />
                            ) : (
                              message
                            )}
                          </Box>
                        </>
                      )}
                    </div>
                  </DropBox>
                </Box>
              ) : (
                <Box data-qa-id={`${qaId}-selector`} mx={-10}>
                  <DropBox
                    position="relative"
                    p={10}
                    onClick={onClick}
                    $hasFile={!!fileId}
                    {...props}
                  >
                    <Box display="flex" alignItems="center" p="6px 16px">
                      <Box ml="4px" mr="8px">
                        <Icon
                          name="paperclip"
                          display="block"
                          width={15}
                          fill="currentColor"
                        />
                      </Box>
                      <BodySmall my={0}>{message}</BodySmall>
                    </Box>
                  </DropBox>
                </Box>
              )}

              {!large && (
                <Box
                  display="inline-block"
                  ml={24}
                  color={colors.battleshipGrey}
                >
                  {fileId ? (
                    <Filename
                      fileId={fileId}
                      filename={filename}
                      qaId={qaId}
                      hideRemove={hideRemove}
                      onChange={onChange}
                      created={showDate ? created : undefined}
                      downloadAttachment={downloadAttachment}
                      disabled={disabled}
                    />
                  ) : (
                    getText('No file chosen')
                  )}
                </Box>
              )}
            </>
          )}
        </DropContainer>
      )}
    </S3Picker>
  );
};

export default FilePicker;
